<template>
  <form
    v-if="enableForceRedirects && preference.country && preference.language"
    ref="multiStoreForm"
    :action="action"
    :method="method"
  >
    <input
      name="_method"
      type="hidden"
      value="put"
    >

    <input
      name="return_to"
      type="hidden"
      :value="returnTo"
    >

    <input
      v-if="preferenceObject?.instance?.url"
      name="no_redirect"
      type="hidden"
      value="true"
    >

    <input
      name="country_code"
      type="hidden"
      :value="preference.country"
    >

    <input
      name="language_code"
      type="hidden"
      :value="preference.language"
    >
  </form>
</template>

<script>

/**
 * Vue: Multi-store (multi-store)
 * -----------------------------------------------------------------------------
 * Used to read country, language, and flag data from section Liquid.
 *
 * @param {Object} current - Current Shopify localization country and language.
 * @param {Boolean} [enableForceRedirects] - Enable force redirects when user
 * preferences don't match current store.
 * @param {Array} instances - Shopify instances.
 * @param {Array} theCountries - Countries to prefix with 'the'.
 * @param {String} [trigger] - Used to set trigger type in Storybook.
 *
 */
import { mapActions, mapState } from 'vuex'

import countriesQuery from '~graphql/countries.gql'
import { formatCountries } from '~/format/format-multi-store'
import { multiStoreAction, multiStoreMethod, multiStoreReturnTo } from '~/helpers/multi-store'

export default {
  name: 'MultiStore',

  props: {
    current: {
      type: Object,
      default: () => ({
        country: {},
        language: {},
      }),
    },
    enableForceRedirects: {
      type: Boolean,
      default: false,
    },
    instances: {
      type: Array,
      default: () => [],
    },
    theCountries: {
      type: Array,
      default: () => [],
    },
    trigger: {
      type: String,
      default: 'geolocation',
    },
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      geolocation: (state) => state['multi-store'].geolocation,
      preference: (state) => state['multi-store'].preference,
      supported: (state) => state['multi-store'].supported,
    }),

    /**
     * Compute form action based on current country code.
     * @returns {String}
     */
    action() {
      return multiStoreAction(this.preferenceObject?.instance)
    },

    /**
     * Compute local suggested store.
     * @returns {Object}
     */
    localSuggestedStore() {
      let countryCode =
        cnvs.multiStore.multiStoreCountry ||
        this.geolocation.country

      if (!cnvs.multiStore.disableMultiStorePreferences && this.preference) {
        countryCode = this.preference.country
      }

      const matchedStore = this.supported[countryCode]

      const matchedLanguage = matchedStore?.languages.find((language) => {
        return language.code.toUpperCase() === this.preference.language
      })

      let language = this.current.language

      if (matchedLanguage) {
        language = matchedLanguage
      } else if (matchedStore?.languages) {
        language = matchedStore.languages[0]
      }

      return {
        country: matchedStore?.country,
        instance: matchedStore?.instance,
        language,
      }
    },

    /**
     * Compute form action based on current country code.
     * @returns {String}
     */
    method() {
      return multiStoreMethod(this.preferenceObject?.instance)
    },

    /**
     * Compute return to URL.
     * @returns {String}
     */
    returnTo() {
      return this.preferenceObject?.instance?.url ? '' : multiStoreReturnTo(this.preferenceObject)
    },

    /**
     * Compute preference object.
     * @returns {Object}
     */
    preferenceObject() {
      const preference = this.supported[this.preference.country]

      const matchedLanguage = preference?.languages.find((language) => {
        return language.code === this.preference.language
      })

      return {
        country: preference?.country,
        instance: preference?.instance,
        language: matchedLanguage,
      }
    },

    /**
     * Compute storage key.
     * @returns {String}
     */
    storageKey() {
      return `multi-store-supported-${cnvs.store.language}`
    },

    /**
     * Compute store to show data from.
     * - In case the user's country isn't supported we suggest the current
     *   store so the form isn't blank.
     * @returns {Object}
     */
    store() {
      return this.localSuggestedStore.country
        ? this.localSuggestedStore
        : this.current
    },
  },

  watch: {

    /**
     * Watch geolocation.
     */
    geolocation() {
      if (cnvs.multiStore.alwaysDisplayMultiStore) {
        this.triggerModal(cnvs.storybook ? this.trigger : 'geolocation')
        return
      }

      if (this.geolocation.country.toUpperCase() === this.current.country.code.toUpperCase()) {
        return
      }

      if (this.supported[this.geolocation.country.toUpperCase()]) {
        this.triggerModal(cnvs.storybook ? this.trigger : 'geolocation')
      }
    },

    /**
     * Watch store to update Vuex state.
     */
    store() {
      this.setSuggestedStore(this.store)
    },
  },

  async mounted() {
    cnvs.EventBus.listen('overlay:open-multi-store-modal', this.triggerOptionsModal)
    this.setCurrent(this.current)
    await this.queryCountries()
    await this.readUrlParameters()

    /**
     * If modal has been dismissed in current session then don't show again,
     * unless country no longer matches the dismissed state.
     */
    if (
      !cnvs.multiStore.alwaysDisplayMultiStore &&
      localStorage.getItem('multi-store-dismissed')
    ) {
      const data = JSON.parse(localStorage.getItem('multi-store-dismissed'))

      if (data.country.toUpperCase() === this.current.country.code.toUpperCase()) {
        return
      }
    }

    /**
     * Check if preferences have been set.
     */
    if (!cnvs.multiStore.disableMultiStorePreferences && this.preference) {
      if (
        !Shopify.designMode &&
        (
          this.preference.country.toUpperCase() !== this.current.country.code.toUpperCase()
        )
      ) {
        if (this.enableForceRedirects) {
          this.$refs.multiStoreForm.submit()
          return
        }

        this.triggerModal(cnvs.storybook ? this.trigger : 'preferences')
      }

      return
    }

    /**
     * Otherwise query geolocation.
     */
    this.queryGeolocation()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      queueModal: 'overlays/queue',
      setCurrent: 'multi-store/setCurrent',
      setGeolocation: 'multi-store/setGeolocation',
      setPreference: 'multi-store/setPreference',
      setSuggestedStore: 'multi-store/setSuggestedStore',
      setSupported: 'multi-store/setSupported',
      setTrigger: 'multi-store/setTrigger',
      setTemplate: 'multi-store/setTemplate',
    }),

    /**
     * Query countries from storefront API.
     */
    queryCountries() {
      return new Promise(async(resolve, reject) => {
        try {

          /**
           * Retrieve from sessionStorage if it's been set.
           */
          if (
            !cnvs.multiStore.disableSavingCountries &&
            sessionStorage.getItem(this.storageKey)
          ) {
            const supported = JSON.parse(sessionStorage.getItem(this.storageKey))
            this.setSupported(supported)
            resolve()
            return
          }

          const response = await cnvs.Query({
            query: countriesQuery,
            variables: {
              country: cnvs.store.country,
              language: cnvs.store.language,
            },
          })

          const supported = formatCountries(response, {
            instances: this.instances,
            theCountries: this.theCountries,
          })

          this.setSupported(supported)

          if (!cnvs.multiStore.disableSavingCountries) {
            sessionStorage.setItem(this.storageKey, JSON.stringify(supported))
          }

          resolve()

        } catch (error) {
          reject(error)
        }
      })
    },

    /**
     * Reads URL parameters if they exist.
     * - Used to set preference to prevent modal from opening if being directed
     *   to an external Shopify instance.
     */
    readUrlParameters() {
      return new Promise(async(resolve) => {
        const parameters = new URLSearchParams(location.search)

        if (!parameters.get('country_code') && !parameters.get('language_code')) {
          resolve()
          return
        }

        this.setPreference({
          country: parameters.get('country_code').toUpperCase(),
          language: parameters.get('language_code').toUpperCase(),
        })

        /**
         * Delete parameters once applied and replace history state.
         */
        parameters.delete('country')
        parameters.delete('country_code')
        parameters.delete('language')
        parameters.delete('language_code')
        parameters.delete('_method')
        parameters.delete('return_to')

        const url = parameters.toString() === ''
          ? `${location.origin}${location.pathname}`
          : `${location.origin}${location.pathname}?${parameters.toString()}`

        history.replaceState({
          html: '',
        }, '', url)

        await this.$nextTick()
        resolve()
      })
    },

    /**
     * Query geolocation from Shopify geolocation app.
     */
    async queryGeolocation() {
      try {
        if (cnvs.storybook) {
          this.setGeolocation({
            detected_values: {
              country: {
                handle: 'GB',
                name: 'United Kingdom',
              },
            },
          })

          return
        }

        let response = await fetch('/browsing_context_suggestions.json')
        response = await response.json()
        this.setGeolocation(response)

      } catch (error) {
        throw new Error('Failed to fetch geolocation', error)
      }
    },

    /**
     * Trigger multi-store modal.
     * @param {String} type - Type of trigger, `geolocation` or `preferences`.
     */
    triggerModal(type) {
      if (this.$variable('multiStore.disable')) {
        return
      }

      this.setTemplate('suggestions')
      this.setTrigger(type)

      this.queueModal({
        component: 'multi-store-modal',
        ignoreDismissed: true,
        namespace: 'multiStoreModal',
      })
    },

    /**
     * Trigger multi-store options modal.
     */
    triggerOptionsModal() {
      if (this.$variable('multiStore.disable')) {
        return
      }

      this.setTemplate('options')

      this.queueModal({
        component: 'multi-store-modal',
        ignoreDismissed: true,
        namespace: 'multiStoreModal',
      })
    },
  },
}
</script>
